<template>
  <div class="locomotive-startup-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>$P = F · v | a = (F-f)/m$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage">
        <svg width="100%" height="160" viewBox="0 0 400 160">
          <!-- 轨道 -->
          <line x1="0" y1="130" x2="400" y2="130" stroke="#e2e8f0" stroke-width="2" />
          
          <!-- 机车 -->
          <g :transform="`translate(${trainX}, 130)`">
             <rect x="-40" y="-35" width="80" height="35" fill="#1e293b" rx="4" />
             <rect x="10" y="-45" width="20" height="15" fill="#334155" rx="2" />
             <circle cx="-25" cy="0" r="6" fill="#64748b" stroke="#fff" stroke-width="1.5" />
             <circle cx="25" cy="0" r="6" fill="#64748b" stroke="#fff" stroke-width="1.5" />
             
             <!-- 尾部速度流线 -->
             <g v-if="running && v > 2">
                <line v-for="i in 3" :key="i" :x1="-45 - i*8" :y1="-10 - i*5" :x2="-55 - i*8" :y2="-10 - i*5" 
                      stroke="#94a3b8" :stroke-width="1" :opacity="v/30" />
             </g>
          </g>
        </svg>

        <div class="hud-panel">
           <div class="hud-item blue">
              <label>实时速度 v</label>
              <span class="val">{{ v.toFixed(1) }} <small>m/s</small></span>
           </div>
           <div class="hud-item orange">
              <label>牵引力 F</label>
              <span class="val">{{ F.toFixed(0) }} <small>N</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
          <div class="control-vertical-group">
             <!-- 行 1: 启动模式 -->
             <div class="mode-selector-row">
                <span class="label">启动工况：</span>
                <el-radio-group v-model="mode" size="small" @change="reset" class="full-row-radio">
                   <el-radio-button label="constPower">恒定功率 P</el-radio-button>
                   <el-radio-button label="constAccel">恒定加速度 a</el-radio-button>
                </el-radio-group>
             </div>
             
             <!-- 行 2: 动作按钮 -->
             <div class="action-row">
                <el-button type="primary" size="small" @click="startToggle" icon="el-icon-video-play">{{ running ? '暂停启动' : '开始启动' }}</el-button>
                <el-button size="small" plain @click="reset" icon="el-icon-refresh-right">重置实验</el-button>
             </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p><strong>物理规律：</strong>{{ mode === 'constPower' ? '恒定功率下，随速度 v 增加，牵引力 F=P/v 减小，加速度也减小，直到 F=f 时达到最大速度。' : '恒定加速度下，由于功率限制，机车先做匀加速运动，当功率达到额定值后转为恒定功率模式。' }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LocomotiveStartupViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'constPower',
      v: 0,
      F: 100,
      Resistance: 40,
      trainX: 50,
      P: 1200, 
      accel: 2.5,
      running: false,
      frameId: null
    }
  },
  methods: {
    startToggle() { this.running = !this.running; },
    reset() {
       this.running = false;
       this.v = 0;
       this.F = this.mode === 'constPower' ? 300 : (this.Resistance + 60 * this.accel);
       this.trainX = 50;
       // No need to call animate() here; it's already a continuous loop from mounted()
    },
    animate() {
       if (this.frameId) cancelAnimationFrame(this.frameId);
       if (this.running) {
          const dt = 0.016; 
          if (this.mode === 'constPower') {
             this.F = this.v > 2 ? (this.P / this.v) : (this.P / 2);
             const a = (this.F - this.Resistance) / 60; 
             this.v += a * dt * 5; 
          } else {
             const pCurrent = (this.Resistance + 60 * this.accel) * this.v;
             if (pCurrent < this.P) {
                this.v += this.accel * dt * 5;
                this.F = this.Resistance + 60 * this.accel;
             } else {
                this.F = this.P / this.v;
                const a = (this.F - this.Resistance) / 60;
                this.v += a * dt * 5;
             }
          }
          // 限制最大显示速度防止溢出
          this.trainX += this.v * 0.15;
          if (this.trainX > 450) this.trainX = -80;
       }
       this.frameId = requestAnimationFrame(this.animate);
    }
  },
  mounted() { this.animate(); },
  beforeDestroy() { if (this.frameId) cancelAnimationFrame(this.frameId); }
}
</script>

<style scoped lang="scss">
.locomotive-startup-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0f7ff; color: #2563eb; border: 1px solid #dbeafe; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 200px; background: #ffffff; border-radius: 12px; border: 1.5px solid #e2e8f0; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: #ffffff; border-radius: 8px; border: 1px solid #e2e8f0; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 14px; color: #1e293b; font-weight: 800; font-family: monospace; small { font-size: 10px; color: #64748b; font-weight: normal; } } &.blue { border-left: 3px solid #3b82f6; } &.orange { border-left: 3px solid #f59e0b; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 12px; color: #64748b; font-weight: 700; white-space: nowrap; } .full-row-radio { display: flex; flex-wrap: nowrap; } } .action-row { display: flex; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 10px; .el-button { flex: 1; font-weight: 700; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
